import { useCallback, useEffect, useState } from 'react';
import type { AdminIslandRow, AdminListResult } from 'minka-shared';
import * as adminApi from '@/services/admin-api';
import { describeAdminError } from '@/utils/api-error';

const EMPTY: AdminListResult<AdminIslandRow> = { rows: [], truncated: false };

export interface UseAdminIslandsResult {
  data: AdminListResult<AdminIslandRow>;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  moveIsland: (roomId: string, organizationId: string) => Promise<void>;
  deleteIsland: (roomId: string) => Promise<void>;
  clearError: () => void;
}

/**
 * The console's Islands tab (§4.3, §6.6): the searched list, and the two things
 * only a superadmin may do to an island — move it to another organization, or
 * delete it.
 *
 * A move answers with the island's rebuilt row and is applied in place; a
 * delete reloads, because the island leaves the list and the organization it
 * left may now be empty, which changes what its group header offers.
 */
export function useAdminIslands(search: string): UseAdminIslandsResult {
  const [data, setData] = useState<AdminListResult<AdminIslandRow>>(EMPTY);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      setData(await adminApi.listIslands(search));
      setError(null);
    } catch (loadError) {
      setError(describeAdminError(loadError, 'Failed to load islands'));
    } finally {
      setIsLoading(false);
    }
  }, [search]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const moveIsland = useCallback(async (roomId: string, organizationId: string) => {
    setError(null);
    try {
      const row = await adminApi.setIslandOrganization(roomId, organizationId);
      setData(previous => ({
        ...previous,
        rows: previous.rows.map(island => (island.id === row.id ? row : island))
      }));
    } catch (moveError) {
      setError(describeAdminError(moveError, 'Failed to move the island'));
      throw moveError;
    }
  }, []);

  const deleteIsland = useCallback(
    async (roomId: string) => {
      setError(null);
      try {
        await adminApi.deleteIsland(roomId);
      } catch (deleteError) {
        setError(describeAdminError(deleteError, 'Failed to delete the island'));
        throw deleteError;
      }
      await refresh();
    },
    [refresh]
  );

  const clearError = useCallback(() => setError(null), []);

  return { data, isLoading, error, refresh, moveIsland, deleteIsland, clearError };
}
